import { useAppConfig } from "~/app/config/useAppConfig";
import { usePriceReferenceSelection } from "~/app/config/useSession";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { isKITProduct } from "~/domains/product/types/ProductData";
import { HousingMessages } from "~/domains/housing/components/HousingMessages";
import type { AddToCartFormDraft, OptionalPartDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useAddToCartDerived } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import { useFormContext } from "react-hook-form";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectIsCartAmountsIsLoading } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import {
  shouldShowPriceReference,
  usePdpSummaryLogic,
} from "~/features/productDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { usePackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { type MouseEvent, useMemo } from "react";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { trackPdpClick, trackPdpWishlistClick } from "~/app/analytics/ga/ga";
import { userIsCustomer, userIsPublic } from "~/domains/user/utils/roles";
import { getRedirectToRegisterPage, redirectToUrl } from "~/utils/url/urlUtils";
import { openAddToWishlistWizard } from "~/features/wishlist/components/modals/AddToWishlist/AddToWishlistService";
import { mapOptionalParts4Wishlist } from "~/features/wishlist/components/modals/AddToWishlist/utils";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "~/utils/constants";
import { Alert } from "~/common/mui/Alert/Alert";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import {
  getPurchaseDataFromUrl,
  selectAmountInCart,
} from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import { Box, Card, Grid, Stack } from "~/common/mui";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { renderButtons } from "~/features/productDetails/components/ProductSummary/components/Buttons/usePdpButtons";
import { TabId } from "~/features/productDetails/components/usePDPTabs";

import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { isTariffSurchargeEnabled } from "~/features/cart/cart/utils/cartUtils";
import MMDAssistant from "~/features/productDetails/components/MMDAssistant/MMDAssistant";
import { useDisplayMMDAssistant } from "~/features/productDetails/context/DisplayMMDAssistantContext";
import { usePdpCmnLogic } from "~/features/productDetails/components/MMDAssistant/logic/usePdpCmnLogic";
import { Product } from "~/domains/product/Product";
import { VariantSelect } from "./components/VariantSelect";
import { useVariantContext } from "../../context/VariantContext";
import { ProductDetailsSection } from "./components/ProductDetailsSection";
import { PdpPriceBlock } from "./components/PdpPriceBlock";

interface Props {
  productDescriptor: ProductDescriptor<PdpPageProductData>;
  hasPreferredProducts: boolean;
  changeTab: (tabId: string) => void;
}

/*
 * Product Details page summary (starting from breadcrumbs, finishing before tabs) for all other cases.
 * Non-salesman, non-distributor - meaning it can be OEM purchaser, registered, anonymous (and others)
 */
export const DefaultPdpSummary = ({ productDescriptor, changeTab }: Props) => {
  const { t } = useTranslation();
  const reco_place = usePurchasePlace();
  const displayMMDAssistant = useDisplayMMDAssistant();

  const product = productDescriptor.base;

  const isHousingKit = isKITProduct(product);
  const { variantLoading } = useVariantContext();

  const formProps = useFormContext<AddToCartFormDraft>();
  const { main, optionalParts: derivedOptionalParts } = useAddToCartDerived();
  const quantity = formProps.watch("quantity");
  const packCode = formProps.watch("selectedPackCode");
  const priceReference = formProps.watch("priceReference");

  const optionalParts = formProps.watch("optionalParts") as Record<string, OptionalPartDraft>;

  const webServicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;
  const isError = productDescriptor.isError && !webServicesDisabled;

  const hasSurcharge = (main.netPrice?.surcharge ?? 0) > 0;
  const tariffSurcharge = isTariffSurchargeEnabled() && (main.netPrice?.tariffSurcharge ?? false);

  const qtyInCart = useSelector(selectAmountInCart({ product: productDescriptor, packCode }));
  const isCartLoading = useSelector(selectIsCartAmountsIsLoading);
  const {
    buttons: { showCartButton, showWishlistButton },
    flags: { showBetterDeal },
  } = usePdpSummaryLogic({
    product: productDescriptor,
    qtyInCart,
    quantity,
    packCode,
  });

  const restrictedPackage = usePackageRestrictionContext() ?? "";

  const { isLoading: isPriceLoading, availability: availabilityInfo } = productDescriptor;
  const isLoading = isPriceLoading || isCartLoading;
  const canSeePrice = userHasPermission(Permission.PRICE);

  const isPMA = isProductPMA(productDescriptor);

  const quantityVisible = product.quantityVisible && !(product.externalProduct && isProductPMA(productDescriptor));
  const isExternalProduct = product.externalProduct;
  const { selection } = usePriceReferenceSelection();
  const isPriceRefVisible = shouldShowPriceReference(isExternalProduct, productDescriptor, selection);

  const { code: productCode, designation, isOptime } = productDescriptor.base;

  const priceInfo = productDescriptor.isMultiPrice ? null : productDescriptor.prices[0];
  const isPriceAt15Digit = productDescriptor.isMultiPrice;

  const { openAddToCartModal, selectClick, openModalWishlist } = useMemo(() => {
    const openAddToCartModal = (e?: MouseEvent<HTMLElement>) => {
      if (e) {
        e.preventDefault();
      }
      const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();
      const context = {
        page: reco_place,
        recommendationPlace,
        recommendationType,
      };
      AddToCartService.openAddToCartWizard(
        {
          productCode,
          restrictionQuery: restrictedPackage,
          initialData: formProps.getValues(),
          source: "pdp",
        },
        context,
      );
    };
    return {
      openAddToCartModal,
      selectClick: () => {
        trackPdpClick("Select Product", designation);
        openAddToCartModal();
      },
      openModalWishlist: (e?: MouseEvent<HTMLElement>) => {
        if (e) {
          e.preventDefault();
        }
        if (userIsPublic()) {
          redirectToUrl(getRedirectToRegisterPage({ openLogin: true }));
        } else {
          trackPdpWishlistClick(designation, 1);
          openAddToWishlistWizard({
            productCode,
            initialQuantity: formProps.getValues("quantity"),
            initialPriceReference: priceReference,
            addTracking: (product) => trackPdpWishlistClick(product.base.designation, 2),
            selectedOptionalParts: mapOptionalParts4Wishlist(productDescriptor.base, optionalParts),
          });
        }
      },
    };
  }, [designation, formProps.getValues, productCode]);

  const qtyInputEnabled =
    !isLoading && ((showCartButton && !isError) || showWishlistButton) && productDescriptor.base.quantityVisible;

  const isContractItem = priceInfo ? priceInfo.isContractAvailable : false;

  let isAddToCartEnabled;
  if (isContractItem) {
    isAddToCartEnabled = userHasPermission(Permission.VIEW_CONTRACT) && userHasPermission(Permission.ADD_TO_CART);
  } else if (!isPriceAt15Digit) {
    isAddToCartEnabled = priceInfo && priceInfo.hasPrice && availabilityInfo && availabilityInfo.hasPackaging;
  } else {
    isAddToCartEnabled = availabilityInfo && availabilityInfo.hasPackaging;
  }

  let addToCartDisabled = !isAddToCartEnabled;

  if (isPMA || !userIsCustomer()) {
    addToCartDisabled = false;
  }
  if (webServicesDisabled || quantity < 1) {
    addToCartDisabled = true;
  }

  if (variantLoading) {
    addToCartDisabled = true;
  }

  const buttons = renderButtons(productDescriptor, {
    atc: {
      disabled: addToCartDisabled,
      onClick: selectClick,
    },
    wishlist: {
      disabled: variantLoading,
      onClick: openModalWishlist,
    },
    configurator: {
      disabled: variantLoading,
      onClick: () => changeTab(TabId.CONFIGURATOR),
    },
    atcFromSP: {
      disabled: variantLoading,
      quantity,
    },
  });

  const mmdCmnData = usePdpCmnLogic();

  const col1 = (
    <Stack gap={2}>
      <Box sx={{ position: "relative" }}>
        {/* {hasSales && <SaleTag className={classesExtended.saleTag} />} */}
        <Product.PrimaryImageWithLogosV2 logoWidth={66} gap={24} size={"1fr"} />
      </Box>
      {isHousingKit && <HousingMessages.NotFinal />}
    </Stack>
  );

  const col2 = <ProductDetailsSection />;

  const col3 = (
    <Card sx={{ p: 2 }}>
      <Stack gap={2}>
        {isError && t("hybrisAjaxError")}

        {isLoading && <CircleSpinner />}

        {!isLoading && canSeePrice && (
          <PdpPriceBlock
            descriptor={productDescriptor}
            packCode={packCode}
            quantity={quantity}
            priceReference={priceReference}
          />
        )}

        {isPriceRefVisible && (
          <PriceReferenceParam
            data-test-id={"pdp-price-reference"}
            priceReference={priceReference}
            onChange={(value) => formProps.setValue("priceReference", value ?? "")}
            product={productDescriptor.base}
            editable={qtyInputEnabled}
          />
        )}

        <VariantSelect />

        {isOptime && <OptimeSolutionsMessage />}

        {quantityVisible && (
          <QuantityInput
            min={1}
            data-test-id={"pdp-input-quantity"}
            max={MAX_QUANTITY}
            value={quantity}
            variant={"compact"}
            disabled={!qtyInputEnabled || variantLoading}
            step={1}
            onValueChange={(newVal) => formProps.setValue("quantity", newVal, { shouldTouch: true, shouldDirty: true })}
          />
        )}

        {showBetterDeal && <Alert severity="info">{t("addProduct.scalingDeal")}</Alert>}

        {!webServicesDisabled && showCartButton && canSeePrice && productDescriptor.isError && (
          <Alert severity="error">{t("addProduct.wsError")}</Alert>
        )}

        <Stack gap={1}>{buttons}</Stack>
        {!tariffSurcharge && <IncludesSurchargeDisclaimer hasSurcharge={hasSurcharge} tariffSurcharge={false} />}
      </Stack>
    </Card>
  );

  return (
    <Grid container columns={12} spacing={3}>
      <Grid size={3}>{col1}</Grid>
      <Grid size={9}>
        <Stack gap={2}>
          {displayMMDAssistant && (
            <MMDAssistant
              marketDesignation={product.marketDesignation}
              initialData={{
                quantity: quantity,
                priceReference: priceReference,
                ...mmdCmnData,
              }}
            />
          )}

          <Grid container columns={9}>
            <Grid size={5}>{col2}</Grid>
            <Grid size={4}>{col3}</Grid>
          </Grid>
        </Stack>
      </Grid>
    </Grid>
  );
};
